import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import VendorConfigFormModal from './VendorConfigFormModal';
import { formatJalaliDate } from '@/lib/format';
import {
    listConfigs,
    updateConfig,
    activateConfig,
    deleteConfig,
} from '@/features/vendor/api/vendorReferralConfigApi';

export default function VendorReferralConfigsPage() {
    const navigate = useNavigate();
    const [configs, setConfigs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [modalMode, setModalMode] = useState(null);
    const [editing, setEditing] = useState(null);

    const load = async () => {
        setLoading(true);
        setError('');
        try {
            const data = await listConfigs();
            setConfigs(data);
        } catch (e) {
            console.error(e);
            setError(e.message || 'خطا در دریافت اطلاعات از سرور');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const openCreate = () => {
        // هدایت به شبیه‌ساز با در نظر گرفتن ساب‌دامین
        const host = window.location.host;
        if (host.includes('.localhost')) {
            const sub = host.split('.')[0];
            window.location.href = `http://${sub}.localhost:8080/vendor/referral-simulator`;
        } else {
            navigate('/vendor/referral-simulator');
        }
    };

    const openEdit = (config) => {
        setEditing(config);
        setModalMode('edit');
    };

    const closeModal = () => {
        setModalMode(null);
        setEditing(null);
    };

    const handleSubmit = async (payload) => {
        setSaving(true);
        setError('');
        setMessage('');
        try {
            if (modalMode === 'edit' && editing) {
                await updateConfig(editing.id, payload);
                setMessage('برنامه پورسانت با موفقیت ویرایش شد.');
            }
            closeModal();
            await load();
        } catch (e) {
            setError(e.message);
            throw e;
        } finally {
            setSaving(false);
        }
    };

    const handleActivate = async (id) => {
        if (!window.confirm('این برنامه فعال شود؟ بقیه برنامه‌های فروشگاه شما غیرفعال خواهند شد.')) return;
        setSaving(true);
        setError('');
        setMessage('');
        try {
            await activateConfig(id);
            setMessage('برنامه با موفقیت فعال شد.');
            await load();
        } catch (e) {
            setError(e.message);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('این برنامه حذف شود؟ این عمل غیرقابل بازگشت است.')) return;
        setSaving(true);
        setError('');
        setMessage('');
        try {
            await deleteConfig(id);
            setMessage('برنامه حذف شد.');
            await load();
        } catch (e) {
            setError(e.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div style={{ direction: 'rtl', padding: '2rem', fontFamily: 'inherit', maxWidth: '1000px', margin: '0 auto' }}>
            <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
                <div>
                    <h1 style={{ fontSize: '1.8rem', margin: 0, color: '#111827' }}>برنامه‌های پورسانت فروشگاه</h1>
                    <p style={{ color: '#6b7280', marginTop: '0.5rem' }}>مدیریت برنامه‌های پورسانت زنجیره‌ای اختصاصی فروشگاه شما</p>
                </div>
                <button
                    onClick={openCreate}
                    style={{ background: '#3b82f6', color: '#fff', border: 'none', padding: '0.75rem 1.5rem', borderRadius: '8px', fontSize: '1rem', fontWeight: 'bold', cursor: 'pointer', boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)' }}
                >
                    + برنامه جدید (از طریق شبیه‌ساز)
                </button>
            </header>

            {message && <div style={{ background: '#dcfce7', color: '#15803d', padding: '1rem', borderRadius: '8px', marginBottom: '1rem', textAlign: 'center' }}>{message}</div>}
            {error && <div style={{ background: '#fee2e2', color: '#b91c1c', padding: '1rem', borderRadius: '8px', marginBottom: '1rem', textAlign: 'center' }}>{error}</div>}

            {loading ? (
                <div style={{ textAlign: 'center', padding: '3rem', fontSize: '1.2rem', color: '#4b5563' }}>در حال بارگذاری...</div>
            ) : configs.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280', background: '#f9fafb', borderRadius: '12px', border: '1px dashed #d1d5db' }}>
                    هنوز هیچ برنامه‌ای ذخیره نکرده‌اید. برای ساخت برنامه، به صفحه «شبیه‌ساز پورسانت» بروید.
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {configs.map((c) => (
                        <div key={c.id} style={{
                            background: '#fff',
                            borderRadius: '12px',
                            border: '1px solid #e5e7eb',
                            padding: '1.5rem',
                            display: 'flex',
                            flexWrap: 'wrap',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                        }}>

                            {/* اطلاعات برنامه */}
                            <div style={{ flex: '1 1 200px', marginBottom: '1rem' }}>
                                <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.1rem', color: '#111827' }}>{c.name}</h3>
                                <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                                    ایجاد شده در: {formatJalaliDate(c.created_at)}
                                </div>
                            </div>

                            {/* پارامترها */}
                            <div style={{ display: 'flex', gap: '1.5rem', flex: '1 1 300px', marginBottom: '1rem', fontSize: '0.9rem', color: '#4b5563' }}>
                                <div><span style={{ color: '#9ca3af' }}>حاشیه سود:</span> {Number(c.min_margin_percent)}%</div>
                                <div><span style={{ color: '#9ca3af' }}>سقف پورسانت:</span> {Number(c.max_total_percent)}%</div>
                                <div><span style={{ color: '#9ca3af' }}>عمق:</span> {c.max_depth} نفر</div>
                            </div>

                            {/* وضعیت */}
                            <div style={{ flex: '0 0 100px', marginBottom: '1rem', textAlign: 'center' }}>
                                {c.is_active ? (
                                    <span style={{ background: '#dcfce7', color: '#15803d', padding: '0.25rem 0.75rem', borderRadius: '999px', fontSize: '0.85rem', fontWeight: 'bold' }}>فعال</span>
                                ) : (
                                    <span style={{ background: '#f3f4f6', color: '#6b7280', padding: '0.25rem 0.75rem', borderRadius: '999px', fontSize: '0.85rem' }}>غیرفعال</span>
                                )}
                            </div>

                            {/* دکمه‌های عملیات */}
                            <div style={{ display: 'flex', gap: '8px', flex: '0 0 auto' }}>
                                <button
                                    onClick={() => openEdit(c)}
                                    style={{ background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe', padding: '0.5rem 1rem', borderRadius: '8px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 'bold' }}
                                >
                                    ویرایش
                                </button>
                                {!c.is_active && (
                                    <button
                                        onClick={() => handleActivate(c.id)}
                                        style={{ background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0', padding: '0.5rem 1rem', borderRadius: '8px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 'bold' }}
                                    >
                                        فعال‌سازی
                                    </button>
                                )}
                                <button
                                    onClick={() => handleDelete(c.id)}
                                    style={{ background: '#fef2f2', color: '#ef4444', border: '1px solid #fecaca', padding: '0.5rem 1rem', borderRadius: '8px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 'bold' }}
                                >
                                    حذف
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {modalMode === 'edit' && (
                <VendorConfigFormModal
                    mode={modalMode}
                    config={editing}
                    onClose={closeModal}
                    onSubmit={handleSubmit}
                    loading={saving}
                />
            )}
        </div>
    );
}